import { useMemo, useRef, useState, type ReactNode, type KeyboardEvent } from 'react'
import { cx } from './cx'
import s from './Matrix.module.css'

export interface MatrixColumn {
  key: string
  label: ReactNode
  /** columns sharing a group string are spanned by one heading above them */
  group?: string
  /** short hover explanation for the column heading */
  title?: string
}

export interface MatrixRow {
  key: string
  label: ReactNode
  /** one-line caption under the row label */
  subtitle?: ReactNode
  /** left-column slot after the label — a Pill, a count */
  meta?: ReactNode
}

export interface MatrixCellPresentation {
  /** Domain state exposed for host-owned text/tone styling and exact tests. */
  state?: string
  /** Finite shared emphasis: the cell is a maximum, never a sum or total. */
  emphasis?: 'maximum'
  /** Optional hover text; the accessible name remains owned by cellLabel. */
  title?: string
}

export interface MatrixProps {
  rows: MatrixRow[]
  columns: MatrixColumn[]
  /** what to draw in a cell. Keep it small — a dot, a check, a short token. */
  cell: (rowKey: string, colKey: string) => ReactNode
  /** click (or Enter/Space) on a cell. Omit for a read-only matrix. */
  onCell?: (rowKey: string, colKey: string) => void
  /** accessible description of a cell's current value, for screen readers */
  cellLabel?: (rowKey: string, colKey: string) => string
  /** finite visual metadata owned by the Matrix cell rather than its content */
  cellPresentation?: (rowKey: string, colKey: string) => MatrixCellPresentation | undefined
  /** heading for the top-left corner */
  corner?: ReactNode
  rowHeaderWidth?: string
  emptyLabel?: string
  className?: string
  testid?: string
}

/**
 * A two-axis grid (the Kendo Grid's locked-column / PivotGrid shape) — rows down
 * one side, columns across the top, one small value per intersection.
 *
 * This is the control a permission editor actually IS: roles × entities, with a
 * scope in each cell. Dataverse scatters that same information across seven
 * screens and a modal, and the reason it feels unmanageable is that you can
 * never see two rows at once. A matrix makes the comparison the default view —
 * "who else has delete on this entity" is a glance, not an investigation.
 *
 * It stays generic on purpose: `cell` renders whatever the caller wants and
 * `onCell` decides what a click means (cycling a scope, toggling a flag). The
 * control owns layout, sticky headers, and keyboard navigation only.
 *
 * Navigation is the roving-tabindex grid pattern — one tab stop for the whole
 * matrix, arrows to move between cells, Enter/Space to act. Without that, a
 * 40 × 6 matrix is 240 tab stops between a keyboard user and the next control.
 */
export function Matrix({
  rows, columns, cell, onCell, cellLabel, cellPresentation, corner, rowHeaderWidth = '18rem',
  emptyLabel = 'Nothing to show.', className, testid,
}: MatrixProps) {
  const [focus, setFocus] = useState<{ r: number; c: number }>({ r: 0, c: 0 })
  const ref = useRef<HTMLTableElement>(null)

  // Column groups render as a spanning header row above the column labels.
  const groups = useMemo(() => {
    if (!columns.some((c) => c.group)) return null
    const out: { label: string; span: number }[] = []
    for (const col of columns) {
      const g = col.group ?? ''
      const last = out[out.length - 1]
      if (last && last.label === g) last.span++
      else out.push({ label: g, span: 1 })
    }
    return out
  }, [columns])

  const focusCell = (r: number, c: number) => {
    const rr = Math.max(0, Math.min(rows.length - 1, r))
    const cc = Math.max(0, Math.min(columns.length - 1, c))
    setFocus({ r: rr, c: cc })
    ;(ref.current?.querySelector(`[data-cell="${rr}-${cc}"]`) as HTMLElement | null)?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    const { r, c } = focus
    switch (e.key) {
      case 'ArrowRight': e.preventDefault(); focusCell(r, c + 1); break
      case 'ArrowLeft': e.preventDefault(); focusCell(r, c - 1); break
      case 'ArrowDown': e.preventDefault(); focusCell(r + 1, c); break
      case 'ArrowUp': e.preventDefault(); focusCell(r - 1, c); break
      case 'Home': e.preventDefault(); focusCell(r, 0); break
      case 'End': e.preventDefault(); focusCell(r, columns.length - 1); break
      case 'Enter': case ' ':
        if (!onCell) return
        e.preventDefault()
        onCell(rows[r]!.key, columns[c]!.key)
        break
    }
  }

  if (!rows.length || !columns.length) return <div className={s.empty}>{emptyLabel}</div>

  return (
    <div className={cx(s.scroll, className)}>
      <table ref={ref} className={s.table} onKeyDown={onKeyDown} data-testid={testid}>
        <thead>
          {groups && (
            <tr className={s.groupRow}>
              <th className={cx(s.corner, s.stickyLeft)} style={{ width: rowHeaderWidth }} />
              {groups.map((g, i) => (
                <th key={`${g.label}-${i}`} colSpan={g.span} className={s.groupHead} scope="colgroup">{g.label}</th>
              ))}
            </tr>
          )}
          <tr>
            <th className={cx(s.corner, s.stickyLeft)} style={{ width: rowHeaderWidth }} scope="col">{corner}</th>
            {columns.map((col) => (
              <th key={col.key} className={s.colHead} scope="col" title={col.title}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={row.key} data-testid={testid ? `${testid}-row-${row.key}` : undefined}>
              <th className={cx(s.rowHead, s.stickyLeft)} scope="row">
                <span className={s.rowLabelWrap}>
                  <span className={s.rowLabel}>{row.label}</span>
                  {row.subtitle && <span className={s.rowSub}>{row.subtitle}</span>}
                </span>
                {row.meta != null && <span className={s.rowMeta}>{row.meta}</span>}
              </th>
              {columns.map((col, c) => {
                const interactive = !!onCell
                const presentation = cellPresentation?.(row.key, col.key)
                return (
                  <td
                    key={col.key}
                    data-cell={`${r}-${c}`}
                    data-testid={testid ? `${testid}-${row.key}-${col.key}` : undefined}
                    tabIndex={focus.r === r && focus.c === c ? 0 : -1}
                    role={interactive ? 'button' : undefined}
                    aria-label={cellLabel?.(row.key, col.key)}
                    data-state={presentation?.state}
                    data-emphasis={presentation?.emphasis}
                    title={presentation?.title}
                    onFocus={() => setFocus({ r, c })}
                    onClick={interactive ? () => onCell!(row.key, col.key) : undefined}
                    className={cx(s.cell, interactive && s.interactive)}
                  >
                    {cell(row.key, col.key)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
